import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import RoutesConfig from './Routes';
import MetaTags from "./HeadMetaTags";
import './styles/main.scss';
import axios from 'axios';
import ReactGA from 'react-ga4';
import { log } from './lib/logger';

const App = () => {
  const [isIOSiPhoneSafari, setIsIOSiPhoneSafari] = useState(false);
  const [isIOSiPadSafari, setIsIOSiPadSafari] = useState(false);
  const [channelData, setChannelData] = useState([]);
  const location = useLocation();

  // Each environment sends to its own GA property (REACT_APP_GA_TRACKING_ID per env file / GitHub
  // environment). Debug mode, which puts every session in GA's DebugView, only when
  // REACT_APP_GA_DEBUG=true (local development).
  useEffect(() => {
    const trackingId = process.env.REACT_APP_GA_TRACKING_ID;
    if (!trackingId) return;
    const debug = process.env.REACT_APP_GA_DEBUG === 'true';
    log.debug('GA Active', { debug });
    ReactGA.initialize(trackingId, debug
      ? { gaOptions: { debug_mode: true }, gtagOptions: { debug_mode: true } }
      : undefined);
  }, []);

  useEffect(() => {
    const checkIOSDevice = () => {
      const userAgent = navigator.userAgent;
      if (userAgent.includes("iPad") && userAgent.includes("Safari")) {
        setIsIOSiPadSafari(true);
      } else if (userAgent.includes("iPhone") && userAgent.includes("Safari")) {
        setIsIOSiPhoneSafari(true);
      }
    };
    checkIOSDevice();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const isChatOnly = urlParams.get('chatOnly');
    if (isChatOnly === 'true') {
      sessionStorage.setItem('chatOnly', 'true');
    } else if (isChatOnly === 'false') {
      sessionStorage.setItem('chatOnly', 'false');
    }
  }, []);

  const parseChannels = (urlUsernames) => {
    const youtubeChannels = [];
    const twitchChannels = [];
    const kickChannels = [];
    const tiktokChannels = [];
    urlUsernames.forEach((username, index) => {
      const channelName = username.substring(1);
      const order = index + 1;
      if (username.startsWith('y')) {
        youtubeChannels.push({ username: channelName, type: 'youtube', order });
      } else if (username.startsWith('t')) {
        twitchChannels.push({ username: channelName, type: 'twitch', order });
      } else if (username.startsWith('k')) {
        kickChannels.push({ username: channelName, type: 'kick', order });
      } else if (username.startsWith('o')) {
        tiktokChannels.push({ username: channelName, type: 'tiktok', order });
      }
    });
    return { youtubeChannels, twitchChannels, kickChannels, tiktokChannels };
  };

  const fetchYouTubeData = async (youtubeChannels) => {
    if (!youtubeChannels) return [];

    const responses = await Promise.all(youtubeChannels.map(async (channel) => {
      const storedVideoID = localStorage.getItem(`youtubeVideoID-${channel.username}`);
      const storedExpiration = localStorage.getItem(`youtubeVideoIDExpiration-${channel.username}`);
      if (storedVideoID && parseInt(storedExpiration) > Date.now()) {
        return {
          username: channel.username,
          videoID: storedVideoID,
          type: 'youtube',
          order: channel.order,
        };
      } else {
        try {
          const response = await axios.get(`${process.env.REACT_APP_YOUTUBE_SERVER}/api/live-status`, {
            params: { username: channel.username },
          });
          const videoID = response.data.videoID || null;
          log.debug('[YouTube] live-status for', channel.username, '→', videoID ?? 'not live');
          if (videoID !== null) {
            localStorage.setItem(`youtubeVideoID-${channel.username}`, videoID);
            localStorage.setItem(`youtubeVideoIDExpiration-${channel.username}`, Date.now() + 15 * 60 * 1000);
          }
          return {
            username: channel.username,
            videoID,
            type: 'youtube',
            order: channel.order,
          };
        } catch (error) {
          console.error('Error fetching live status for YouTube channel:', channel.username, error);
          return {
            username: channel.username,
            videoID: null,
            type: 'youtube',
            order: channel.order,
          };
        }
      }
    }));

    return responses.filter(Boolean);
  };

  useEffect(() => {
    const fetchData = async () => {
      const urlUsernames = location.pathname.split('/').filter(Boolean);
      const { youtubeChannels, twitchChannels, kickChannels, tiktokChannels } = parseChannels(urlUsernames);
      const youtubeData = await fetchYouTubeData(youtubeChannels);

      const twitchData = twitchChannels.filter(channel => channel.type === 'twitch').map(channel => ({
        username: channel.username,
        type: 'twitch',
        order: channel.order,
      }));

      const kickData = kickChannels.filter(channel => channel.type === 'kick').map(channel => ({
        username: channel.username,
        type: 'kick',
        order: channel.order,
      }));

      const tiktokData = tiktokChannels.filter(channel => channel.type === 'tiktok').map(channel => ({
        username: channel.username,
        type: 'tiktok',
        order: channel.order,
      }));

      setChannelData([...youtubeData, ...twitchData, ...kickData, ...tiktokData]);
    };
    fetchData();
  }, [location.pathname]);

  return (
    <div className={`watch-streams ${isIOSiPhoneSafari ? 'ios-iphone-safari' : ''} ${isIOSiPadSafari ? 'ios-ipad-safari' : ''}`}>
      <MetaTags channelData={channelData}/>
      <RoutesConfig channelData={channelData} />
    </div>
  );
};

export default App;
